/**
 * template/selfChromedSections.js
 *
 * Single source of truth for "which section designs ship their own site chrome".
 *
 * Some approved page designs (the Exam Portal, for example) include the site nav and
 * footer as part of the artwork. Those pages must NOT also receive the shared chrome,
 * otherwise the page renders two headers and two footers.
 *
 * Chrome is rendered in two independent places, so both must consult this module:
 *   1. container/Layout.jsx  — the app shell chrome around every routed page
 *   2. template/TemplateRenderer.jsx — the per-template chrome
 *
 * It lives in its own module (rather than in TemplateRenderer) because Layout and
 * EducationThemeBridge would otherwise form an import cycle with TemplateRenderer.
 *
 * The mirror of this list on the Console side is `selfChromedSectionTypes` in
 * admin-console-ui/src/modules/template-builder/ConsoleEducationPreview.jsx.
 */

export const SELF_CHROMED_SECTION_TYPES = Object.freeze([
  'educationExamHome',
  'educationExamCourses',
  'educationExamCourseDetail',
  'educationExamAbout',
  'educationExamContact',
  'educationExamInfo',
  'educationExamShell'
]);

const selfChromedTypes = new Set(SELF_CHROMED_SECTION_TYPES);

export const isSelfChromedSectionType = (type) => selfChromedTypes.has(type);

/** True when any visible section on the page renders its own nav/footer. */
export const pageEmbedsOwnChrome = (page) => (
  Array.isArray(page?.sections)
  && page.sections.some((section) => section?.visible !== false && selfChromedTypes.has(section?.type))
);

/** Normalizes a route or pathname so '/course/' and '/Course' compare equal. */
export const normalizeChromeRoute = (route = '') => {
  const cleaned = String(route || '').trim().toLowerCase().replace(/\/+$/, '');
  if (!cleaned) return '/';
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
};

/**
 * Resolves whether the template page served at `pathname` embeds its own chrome.
 *
 * @param {object} template a normalized template (`{ pages: [{ route, sections }] }`)
 * @param {string} pathname the current location pathname
 */
export const templatePageEmbedsOwnChrome = (template, pathname) => {
  const pages = template?.pages;
  if (!Array.isArray(pages) || !pages.length) return false;

  const target = normalizeChromeRoute(pathname);
  const page = pages.find((item) => normalizeChromeRoute(item?.route) === target);
  return pageEmbedsOwnChrome(page);
};
